import React, { useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight, BookOpen } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { config } from '../../config'
import { useAuth } from '../../AuthProvider'

interface Book {
  id: string
  title: string
  author?: string
  cover_url?: string
  created_at: string
  isbn?: string
}

export const BookCarousel: React.FC = () => {
  const [books, setBooks] = useState<Book[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [loading, setLoading] = useState(true)
  const { session } = useAuth()
  const navigate = useNavigate()
  
  // Number of books to show at once
  const booksPerView = window.innerWidth < 640 ? 1 : window.innerWidth < 1024 ? 2 : 3

  useEffect(() => {
    loadBooks()
  }, [session])

  const loadBooks = async () => {
    if (!session?.access_token) return
    
    try {
      const response = await fetch(`${config.API_BASE}/api/books`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        // Sort by created_at descending to show newest first
        const sortedBooks = data.sort((a: Book, b: Book) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )
        setBooks(sortedBooks)
      }
    } catch (error) {
      console.error('Error loading books:', error)
    } finally {
      setLoading(false)
    }
  }

  const handlePrevious = () => {
    setCurrentIndex(prev => Math.max(0, prev - booksPerView))
  }

  const handleNext = () => {
    setCurrentIndex(prev => Math.min(books.length - booksPerView, prev + booksPerView))
  }

  const handleBookClick = (bookId: string) => {
    navigate(`/qr/dash/content?edit=${bookId}&type=book&focus=true`)
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-32 mb-4"></div>
          <div className="flex gap-4">
            <div className="w-32 h-48 bg-gray-200 rounded"></div>
            <div className="w-32 h-48 bg-gray-200 rounded"></div>
            <div className="w-32 h-48 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    )
  }

  if (books.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center mb-4">
          <BookOpen className="h-5 w-5 mr-2" />
          Ihre Bücher
        </h3>
        <p className="text-gray-500 text-center py-8">
          Noch keine Bücher erstellt. 
          <button 
            onClick={() => navigate('/qr/dash/content?create=book')}
            className="text-blue-600 hover:underline ml-1"
          >
            Erstes Buch anlegen
          </button>
        </p>
      </div>
    )
  }

  const visibleBooks = books.slice(currentIndex, currentIndex + booksPerView)
  const canGoPrevious = currentIndex > 0
  const canGoNext = currentIndex + booksPerView < books.length

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <BookOpen className="h-5 w-5 mr-2" />
          Ihre Bücher
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrevious}
            disabled={!canGoPrevious}
            className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="text-sm text-gray-500">
            {currentIndex + 1}-{Math.min(currentIndex + booksPerView, books.length)} von {books.length}
          </span>
          <button
            onClick={handleNext}
            disabled={!canGoNext}
            className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {visibleBooks.map((book) => (
          <div
            key={book.id}
            onClick={() => handleBookClick(book.id)}
            className="cursor-pointer group"
          >
            <div className="relative overflow-hidden rounded-lg shadow-md hover:shadow-lg transition-shadow aspect-[3/4]">
              {book.cover_url ? (
                <img
                  src={book.cover_url}
                  alt={book.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                  <BookOpen className="w-12 h-12 text-gray-400" />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            <div className="mt-2">
              <h4 className="font-medium text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                {book.title}
              </h4>
              {book.author && (
                <p className="text-sm text-gray-500 truncate">{book.author}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}